import { clsx } from 'clsx'
import { ArrowLeft } from 'lucide-react'
import { validateRedirect } from '@/shared/lib/auth'
import { useRegisterFlow } from '../hooks/useRegisterFlow'
import { EmailStep } from './EmailStep'
import { OtpStep } from './OtpStep'
import { ProfileStep } from './ProfileStep'
import styles from './EntrarPage.module.css'

type EntrarPageProps = {
  redirectParam: string | undefined
  inviteCode: string | undefined
  authError: string | undefined
}

// ── Left value-prop column (desktop only) ─────────────────────────────────────

const FEATURES = [
  {
    icon: '🏆',
    title: 'Regras 100% suas',
    sub: 'Pontuação, formato, transferências — você define cada detalhe',
  },
  {
    icon: '⚡',
    title: 'Pontuação ao vivo',
    sub: 'Cada gol vira ponto na hora, sem refresh',
  },
  {
    icon: '✦',
    title: 'Sobrevivente',
    sub: 'Eliminatório semanal exclusivo do Fantyx',
  },
  {
    icon: '🎮',
    title: 'Multi-esporte',
    sub: 'Futebol agora, CS2 e basquete em breve',
  },
] as const

function LeftColumn() {
  return (
    <div className={styles.leftCol} aria-hidden="true">
      <div className={styles.eyebrow}>
        <span className={styles.eyebrowDot} />
        Copa de Seleções 2026
      </div>
      <h2 className={styles.leftHeading}>
        Tua liga,{' '}
        <br />
        tuas <span className={styles.leftHeadingAccent}>regras</span>.
      </h2>
      <p className={styles.leftSub}>
        Cria ligas com regras 100% suas, escolhe entre 3 formatos — incluindo o
        Sobrevivente, eliminatório exclusivo. Grátis pra começar.
      </p>
      <ul className={styles.featureList}>
        {FEATURES.map(feature => (
          <li key={feature.title} className={styles.featureItem}>
            <span className={styles.featureIcon} aria-hidden="true">{feature.icon}</span>
            <div>
              <p className={styles.featureText}>{feature.title}</p>
              <p className={styles.featureTextSub}>{feature.sub}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Invite band shown at top of card when a join code is in the URL ───────────

type InviteBandProps = { code: string }

function InviteBand({ code }: InviteBandProps) {
  return (
    <div className={styles.inviteBand} role="status" aria-label="Convite para liga">
      <span className={styles.inviteBandIcon} aria-hidden="true">🏆</span>
      <div>
        <p className={styles.inviteBandName}>Você foi convidado</p>
        <p className={styles.inviteBandSub}>
          Entre para a liga com o código{' '}
          <span className={styles.inviteCode}>{code}</span>
        </p>
      </div>
    </div>
  )
}

// ── Logo mark shown inside the card on mobile ─────────────────────────────────

function LogoMark() {
  return (
    <div className={styles.logoWrap} aria-label="Fantyx.Pro">
      <svg
        className={styles.logoMark}
        viewBox="0 0 120 120"
        aria-hidden="true"
        focusable="false"
      >
        <polygon points="14,14 38,14 106,106 82,106" fill="var(--acc)" />
        <polygon points="14,106 30,106 106,30 106,14 90,14 14,90" fill="var(--text-1)" />
      </svg>
      <div>
        <span className={styles.logoMain}>FANTYX.PRO</span>
        <span className={styles.logoSub}>Copa de Seleções 2026</span>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function EntrarPage({ redirectParam, inviteCode, authError }: EntrarPageProps) {
  const redirectDest = redirectParam ? (validateRedirect(redirectParam) ?? '/home') : '/home'
  const initialError = authError
    ? 'Ocorreu um erro ao entrar. Tente novamente.'
    : undefined
  const flow = useRegisterFlow(redirectDest, initialError)

  return (
    <div className={styles.page}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.layout}>
        <LeftColumn />

        <div className={styles.card}>
          {inviteCode && <InviteBand code={inviteCode} />}

          <div className={styles.cardBody}>
            {/* Back button — invisible on step 1 but still in DOM for layout stability */}
            <button
              type="button"
              className={clsx(styles.backBtn, flow.step > 1 && styles.backBtnVisible)}
              onClick={flow.goBack}
              aria-label="Voltar"
              tabIndex={flow.step === 1 ? -1 : 0}
            >
              <ArrowLeft size={16} color="var(--text-secondary)" />
            </button>

            {/* Logo shown on mobile (TopNav not visible inside card) */}
            <LogoMark />

            {/* Steps — keyed so React remounts each on transition, firing entry animation */}
            {flow.step === 1 && <EmailStep key="step-email" flow={flow} />}
            {flow.step === 2 && <OtpStep key="step-otp" flow={flow} />}
            {flow.step === 3 && <ProfileStep key="step-profile" flow={flow} />}
          </div>
        </div>
      </div>
    </div>
  )
}
